import { useCallback, useEffect, useState } from 'react';
import { Card, Button, Input, Alert, Badge, Loader, Tabs, Checkbox } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { getApiRoot } from '@/lib/domainResolver';

// What the server sends instead of a secret, and what it accepts back to mean "unchanged".
const MASK = '__SET__';

const GROUPS = [
  { key: 'storage', label: 'Storage', blurb: 'DigitalOcean Spaces — screenshots, videos and compressed outputs.' },
  { key: 'github', label: 'GitHub', blurb: 'OAuth app and GitHub App credentials for the repository integration.' },
  { key: 'agent', label: 'Agent', blurb: 'Settings for the coding agent.' },
  { key: 'runtime', label: 'Runtime', blurb: 'Redis, background workers and per-feature limits.' },
];

const SOURCE_VARIANT = { database: 'success', environment: 'info', default: 'default', unset: 'warning' };
const SOURCE_LABEL = { database: 'saved here', environment: 'from environment', default: 'default', unset: 'not set' };

export default function Settings() {
  const { fetchApi } = useAppApi();
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});   // key -> string, only for edited fields
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // the group being saved
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${getApiRoot()}/api/meta/settings`, { credentials: 'include' });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `Could not load settings (HTTP ${res.status})`);
      setFields(body.fields || []);
      // Seed only the non-secret values; a secret starts blank and stays blank unless typed.
      const seed = {};
      for (const f of body.fields || []) if (!f.secret) seed[f.key] = f.value ?? '';
      setValues(seed);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveGroup = async (group) => {
    setSaving(group);
    setErr('');
    setSaved('');
    try {
      const entries = fields
        .filter((f) => f.group === group)
        // An untouched secret is simply omitted, which is what leaves it unchanged. Sending
        // the mask back would be refused by the model, deliberately.
        .filter((f) => !(f.secret && !values[f.key]))
        .map((f) => ({ key: f.key, value: values[f.key] ?? '' }));

      await fetchApi('crud', { model: 'Setting', action: 'save', data: { settings: entries } });
      setSaved(group);
      // Re-read so the source badges reflect what actually landed, and secrets go back to
      // masked — the browser should never keep holding one.
      setValues((v) => {
        const next = { ...v };
        for (const f of fields) if (f.secret && f.group === group) delete next[f.key];
        return next;
      });
      await load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <Loader text="Loading settings…" />;

  const renderField = (f) => {
    const isBool = f.type === 'boolean';
    if (isBool) {
      const checked = values[f.key] === '1' || values[f.key] === 'true' || values[f.key] === true;
      return (
        <div key={f.key} className="py-2">
          <Checkbox
            label={f.label}
            checked={checked}
            onChange={(e) => {
              const on = e?.target ? e.target.checked : Boolean(e);
              setValues((v) => ({ ...v, [f.key]: on ? '1' : '' }));
            }}
          />
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={SOURCE_VARIANT[f.source]}>{SOURCE_LABEL[f.source]}</Badge>
            <code className="text-[11px] opacity-50">{f.key}</code>
          </div>
          {f.help ? <p className="text-xs opacity-60 mt-1">{f.help}</p> : null}
        </div>
      );
    }

    return (
      <div key={f.key} className="py-2">
        <Input
          label={f.label}
          type={f.type === 'number' ? 'number' : 'text'}
          value={values[f.key] ?? ''}
          placeholder={f.secret && f.isSet ? MASK : ''}
          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
        />
        <div className="flex items-center gap-2 mt-1">
          <Badge variant={SOURCE_VARIANT[f.source]}>{SOURCE_LABEL[f.source]}</Badge>
          <code className="text-[11px] opacity-50">{f.key}</code>
        </div>
        {f.secret && f.isSet ? (
          <p className="text-xs opacity-60 mt-1">A value is stored. Leave blank to keep it; type to replace it.</p>
        ) : null}
        {f.help ? <p className="text-xs opacity-60 mt-1">{f.help}</p> : null}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold">Settings</h1>
        <p className="text-sm opacity-70">
          Values saved here are stored in the database and override the environment. Clear one to
          fall back to its environment variable.
        </p>
      </div>

      {err ? <Alert variant="error" title="Could not save">{err}</Alert> : null}

      <Tabs>
        <Tabs.List>
          {GROUPS.map((g) => <Tabs.Tab key={g.key}>{g.label}</Tabs.Tab>)}
        </Tabs.List>
        <Tabs.Panels>
          {GROUPS.map((g) => {
            const groupFields = fields.filter((f) => f.group === g.key);
            const needsRestart = groupFields.some((f) => f.boot);
            const unwired = groupFields.length > 0 && groupFields.every((f) => !f.wired);
            return (
              <Tabs.Panel key={g.key}>
                <Card className="p-4 mt-2">
                  <p className="text-sm opacity-70 mb-2">{g.blurb}</p>

                  {/* Say what a value will and won't do, rather than letting someone save one
                      and conclude the app is broken when nothing changes. */}
                  {unwired ? (
                    <Alert variant="warning" title="Stored, but not used yet">
                      Nothing reads these values today — the coding agent that would consume them
                      is not implemented in this app. Saving them is harmless and forward-looking.
                    </Alert>
                  ) : null}
                  {needsRestart ? (
                    <Alert variant="info" title="Takes effect on restart">
                      These are read once when the process starts, so a change here does not move a
                      running worker or reconnect Redis until the app is restarted.
                    </Alert>
                  ) : null}

                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {groupFields.map(renderField)}
                  </div>

                  <div className="flex items-center gap-3 mt-4">
                    <Button onClick={() => saveGroup(g.key)} disabled={saving === g.key}>
                      {saving === g.key ? 'Saving…' : `Save ${g.label.toLowerCase()} settings`}
                    </Button>
                    {saved === g.key ? <span className="text-sm text-success-600">Saved.</span> : null}
                  </div>
                </Card>
              </Tabs.Panel>
            );
          })}
        </Tabs.Panels>
      </Tabs>
    </div>
  );
}
